import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ScrollLines, Reveal, Eyebrow } from '@/components/Reveal';
import CtaBand from '@/components/CtaBand';

const FARM_IMG =
  'https://images.pexels.com/photos/16166178/pexels-photo-16166178.jpeg?auto=compress&cs=tinysrgb&w=1600';

const TURMERIC_GALLERY = [
  {
    src: '/images/turmeric-harvest.jpg',
    caption: 'Harvest · Fresh fingers lifted from red soil',
    span: 'md:col-span-5 h-[360px] md:h-[520px]',
  },
  {
    src: '/images/turmeric-drying.jpg',
    caption: 'Curing · Boiled fingers sun-dried in the yard',
    span: 'md:col-span-7 h-[320px] md:h-[520px]',
  },
];

const VALUES = [
  ['Balance', 'The equinox is the day night and day stand equal. We trade the same way — a price fair to the farmer and to the buyer, at the same time.'],
  ['Transparency', 'Origin, grade, lab report, terms. Every layer of the trade is visible to the buyer before a rupee changes hands.'],
  ['Seasonality', 'Spice markets breathe with harvests. We buy in season, store clean, and sell into demand — timing is part of the price.'],
];

const PROCESS = [
  ['01', 'Source', 'Contracted farmer groups and estates across the turmeric belt and the cardamom hills — Salem, Erode, Sangli, Idukki and Wayanad.'],
  ['02', 'Clean & grade', 'Dust, stone and moisture removed; lots graded to millimetre and mesh at origin facilities.'],
  ['03', 'Test', 'Independent lab checks — curcumin, ASTA, moisture, micro — before a lot is cleared to ship.'],
  ['04', 'Ship', 'Container or LCL on FOB terms, fully documented to the port of loading. The certificate travels with the cargo.'],
];

export default function About() {
  const storyRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: storyRef, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  return (
    <div className="bg-night text-bone">
      <header className="mx-auto max-w-7xl px-6 pb-20 pt-40 md:px-12 md:pb-28 md:pt-48">
        <Reveal>
          <Eyebrow>Our story</Eyebrow>
        </Reveal>
        <ScrollLines
          className="mt-6 block"
          lineClassName="font-serif text-5xl font-medium leading-[1.0] tracking-tight text-bone sm:text-6xl lg:text-7xl"
          lines={[
            'Named for the day',
            <>everything is <em className="text-turmeric">in balance.</em></>,
          ]}
        />
        <Reveal delay={0.3} className="mt-10 max-w-2xl">
          <p className="text-base leading-relaxed text-dune md:text-lg">
            Twice a year, the equinox balances day and night across the whole planet. EQUINOX
            SPICES was founded in <span className="text-bone">2021</span> by{' '}
            <span className="text-bone">TJ Mohan Raja</span>, with business partner{' '}
            <span className="text-bone">Abishek Pandian</span>, to bring that same balance to the
            spice trade — between the farm gate and the destination port, between quality and
            price.
          </p>
        </Reveal>
      </header>

      {/* SOURCING */}
      <section ref={storyRef} className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32" data-testid="about-sourcing">
        <div className="grid items-center gap-10 md:grid-cols-12 md:gap-14">
          <Reveal className="md:col-span-7">
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <motion.img
                src={FARM_IMG}
                alt="Farmers collecting cardamom at a plantation in Wayanad, India"
                loading="lazy"
                style={{ y: imgY }}
                className="h-[420px] w-full scale-110 object-cover md:h-[560px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-night/70 via-transparent to-transparent" />
              <p className="absolute bottom-6 left-6 text-[11px] uppercase tracking-[0.26em] text-bone/85">
                Cardamom harvest · Wayanad, India
              </p>
            </div>
          </Reveal>
          <div className="md:col-span-5">
            <ScrollLines
              className="block"
              lineClassName="font-serif text-3xl font-medium leading-[1.1] text-bone sm:text-4xl"
              lines={['We trade where', 'the spice grows.']}
            />
            <Reveal delay={0.15} className="mt-6 space-y-5 text-sm leading-relaxed text-dune md:text-base">
              <p>
                No commission chains, no anonymous lots. Our team stands at the drying yards and
                auction floors, contracts with farmer groups and estates, and walks every
                consignment from the field to the container.
              </p>
              <p>
                Because we buy at origin — in the season — our buyers access origin prices without
                flying there themselves. That is the arbitrage we exist for, and it is what makes
                our quotes hard to beat.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TURMERIC GALLERY */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32" data-testid="about-turmeric-gallery">
        <Reveal>
          <Eyebrow>Turmeric, field to sack</Eyebrow>
        </Reveal>
        <ScrollLines
          className="mt-6 block"
          lineClassName="font-serif text-4xl font-medium leading-[1.05] text-bone sm:text-5xl"
          lines={['Every finger we ship', 'we have seen.']}
        />
        <div className="mt-14 grid gap-6 md:grid-cols-12">
          {TURMERIC_GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={0.08 * i} className={g.span}>
              <figure className="group relative h-full overflow-hidden rounded-3xl border border-white/10" data-testid={`gallery-item-${i}`}>
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-night/10 to-transparent" />
                <figcaption className="absolute bottom-6 left-6 text-[11px] uppercase tracking-[0.26em] text-bone/85">
                  {g.caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="border-y border-white/10 bg-coal/30">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <Reveal>
            <Eyebrow>What we hold to</Eyebrow>
          </Reveal>
          <ScrollLines
            className="mt-6 block"
            lineClassName="font-serif text-4xl font-medium leading-[1.05] text-bone sm:text-5xl"
            lines={['Three values, weighed daily.']}
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {VALUES.map(([title, body], i) => (
              <Reveal key={title} delay={0.08 * i}>
                <div className="h-full rounded-3xl border border-white/10 bg-night/60 p-8 transition-transform duration-500 hover:-translate-y-1 md:p-10" data-testid={`about-value-${title.toLowerCase()}`}>
                  <span className="font-serif text-lg italic text-turmeric">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-3xl font-medium text-bone">{title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-dune">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="about-process">
        <div className="grid gap-12 md:grid-cols-12 md:gap-14">
          <div className="md:col-span-5">
            <Reveal>
              <Eyebrow>The route</Eyebrow>
            </Reveal>
            <ScrollLines
              className="mt-6 block"
              lineClassName="font-serif text-4xl font-medium leading-[1.05] text-bone sm:text-5xl"
              lines={['From yard', 'to vessel.']}
            />
            <Reveal delay={0.15} className="mt-8 max-w-md text-sm leading-relaxed text-dune md:text-base">
              <p>
                Four steps, one standard. Every lot follows the same route so the buyer knows
                exactly what arrives — and what it was tested for — before it leaves India.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <div className="flex flex-col">
              {PROCESS.map(([n, title, body], i) => (
                <Reveal key={n} delay={0.06 * i}>
                  <div className="flex gap-6 border-b border-white/10 py-8 first:pt-0 last:border-b-0">
                    <span className="font-serif text-2xl italic text-turmeric/80">{n}</span>
                    <div>
                      <h3 className="font-serif text-2xl font-medium text-bone">{title}</h3>
                      <p className="mt-2 max-w-lg text-sm leading-relaxed text-dune">{body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
